import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, connect } from "react-redux";
import { CernFooter, LoginRequired } from "@authzsvc/common-react-components";
import { Routes, Route, NavLink, useParams } from "react-router-dom";
import { logout } from "./actions/api";
import MyResources from "./components/managedResources/MyResources";
import { Flex, Menu, Layout } from "antd";
import {
  AppstoreOutlined,
  SettingOutlined,
  OrderedListOutlined,
} from "@ant-design/icons";
import HeaderMenuItems from "./components/headers/HeaderMenuItems";
import Breadcrumbs from "./components/headers/Breadcrumbs";
import RequiredActionsWrapper from "./components/requiredActions/RequiredActionsWrapper";
import RequestDetailsWrapper from "./components/requests/RequestDetailsWrapper";
import { getMyRequests } from "./actions";
import ServiceCollectionList from "./components/serviceCollection/ServiceCollectionList";
import ServiceApplicationsList from "./components/serviceApplication/ServiceApplicationsList";
import { SiteHeader } from "./components/headers/PageHeaders";
import styles from "./LoadedApp.module.css";
import ServiceCollectionListAdmin from "./components/admin/ServiceCollectionListAdmin";
import ServiceApplicationsListAdmin from "./components/admin/ServiceApplicationsListAdmin";
import ServiceCollectionEligibilitySettingsAdmin from "./components/admin/ServiceCollectionEligibilitySettingsAdmin";
import ServiceApplicationSubscriptionSettingsAdmin from "./components/admin/ServiceApplicationSubscriptionSettingsAdmin";
import ServiceApplicationManagedResourceTypeAdmin from "./components/admin/ServiceApplicationManagedResourceTypeAdmin";
import ManagedResourcesListAdmin from "./components/admin/ManagedResourceTypesListAdmin";
import WizardAdmin from "./components/admin/wizard/WizardAdmin";
import RedirectPage from "./RedirectPage";

const LoadedApp = ({ authenticated }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getMyRequests());
  }, [dispatch]);

  const WrapWithMenu = ({ component: Component, keySelector }) => {
    const params = useParams();
    const key = keySelector ? keySelector(params) : undefined;

    return (
      <Flex gap="middle" vertical>
        <SiteHeader
          title="CERN Computing Resources"
          subTitle="Manage your computing resources and subscriptions to CERN Services"
        />
        <Menu
          mode="horizontal"
          defaultSelectedKeys={[
            window.location.pathname.split("/")[1] || "service",
          ]}
          items={[
            {
              key: "service",
              icon: <AppstoreOutlined />,
              label: <NavLink to="/">Services</NavLink>,
            },
            {
              key: "my",
              icon: <OrderedListOutlined />,
              label: <NavLink to="/my">My Resources</NavLink>,
            },
            {
              key: "manage",
              icon: <SettingOutlined />,
              label: "Manage",
              children: [
                {
                  key: "manage:1",
                  label: (
                    <NavLink to="/wizard">Add a Service Application</NavLink>
                  ),
                },
                {
                  key: "manage:2",
                  label: (
                    <NavLink to="/manage">Advanced Administration</NavLink>
                  ),
                },
              ],
            },
          ]}
        ></Menu>
        <Component key={key} />
      </Flex>
    );
  };

  WrapWithMenu.propTypes = {
    component: PropTypes.object,
    keySelector: PropTypes.func,
  };

  const RequestDetailsWrapperWrapper = () => {
    const params = useParams();
    return (
      <>
        <Breadcrumbs />
        <RequestDetailsWrapper
          id={params.id}
          breadcrumbs={[
            ["/required-actions", "Required actions"],
            [
              // eslint-disable-next-line react/prop-types
              `/required-actions/${params.id}`,
              // eslint-disable-next-line react/prop-types
              params.id,
            ],
          ]}
        />
      </>
    );
  };

  const mainRouter = (
    <Routes>
      <Route
        path="/"
        element={<WrapWithMenu component={ServiceCollectionList} />}
      />
      <Route path="/my" element={<WrapWithMenu component={MyResources} />} />

      {/* Danger zone, Manage vs manage, hard map to MapSSH Linux and AFS manage */}
      <Route
        path="/Manage/ListServices.aspx"
        element={
          <RedirectPage url="https://resources-old.web.cern.ch/resources-old/Manage/ListServices.aspx" />
        }
      />
      <Route
        path="/Manage/Linux/:subPath1"
        element={
          <RedirectPage url="https://resources-old.web.cern.ch/resources-old/Manage/Linux" />
        }
      />
      <Route
        path="/Manage/AFS/:subPath1"
        element={
          <RedirectPage url="https://resources-old.web.cern.ch/resources-old/Manage/AFS" />
        }
      />
      <Route
        path="/Manage/Accounts/MapSSH.aspx"
        element={
          <RedirectPage url="https://resources-old.web.cern.ch/resources-old/Manage/Accounts/MapSSH.aspx" />
        }
      />

      <Route
        path="/manage"
        element={<WrapWithMenu component={ServiceCollectionListAdmin} />}
      />
      <Route
        path="/wizard"
        element={<WrapWithMenu component={WizardAdmin} />}
      />
      <Route
        path="/service/:serviceCollectionId"
        element={
          <WrapWithMenu
            component={ServiceApplicationsList}
            keySelector={(p) => p.serviceCollectionId}
          />
        }
      />
      <Route
        path="/manage/:serviceCollectionId"
        element={
          <WrapWithMenu
            component={ServiceApplicationsListAdmin}
            keySelector={(p) => p.serviceCollectionId}
          />
        }
      />
      <Route
        path="/manage/:serviceCollectionId/edit"
        element={
          <WrapWithMenu
            component={ServiceCollectionEligibilitySettingsAdmin}
            keySelector={(p) => p.serviceCollectionId}
          />
        }
      />
      <Route
        path="/manage/:serviceCollectionId/application/:applicationId"
        element={
          <WrapWithMenu
            component={ServiceApplicationSubscriptionSettingsAdmin}
            keySelector={(p) => p.applicationId}
          />
        }
      />
      <Route
        path="/manage/:serviceCollectionId/application/:applicationId/managedresourcetype"
        element={
          <WrapWithMenu
            component={ServiceApplicationManagedResourceTypeAdmin}
            keySelector={(p) => p.applicationId}
          />
        }
      />
      <Route
        path="/manage/:serviceCollectionId/application/:applicationId/managedresources"
        element={
          <WrapWithMenu
            component={ManagedResourcesListAdmin}
            keySelector={(p) => p.applicationId}
          />
        }
      />

      <Route
        path="/required-actions"
        element={
          <RequiredActionsWrapper
            breadcrumbs={[["/required-actions", "Required actions"]]}
          />
        }
      />
      <Route
        path="/required-actions/:id"
        element={<RequestDetailsWrapperWrapper />}
      />

      {/* Legacy redirections */}
      <Route
        path="/resources/Manage/:subPath1/:subPath2?"
        element={
          <RedirectPage url="https://resources-old.web.cern.ch/resources-old/Manage" />
        }
      />
    </Routes>
  );

  return (
    <Layout style={{ minHeight: "100vh", backgroundColor: "white" }}>
      <Layout.Header
        style={{
          position: "sticky",
          zIndex: 1,
        }}
      >
        <HeaderMenuItems authenticated={authenticated} />
      </Layout.Header>
      <Layout.Content className={styles.LayoutContent}>
        {authenticated ? mainRouter : <LoginRequired />}
      </Layout.Content>
      <Layout.Footer style={{ padding: "0px" }}>
        <CernFooter />
      </Layout.Footer>
    </Layout>
  );
};

LoadedApp.propTypes = {
  authenticated: PropTypes.bool.isRequired,
};

export default connect(
  (state) => ({
    authenticated: state.keycloak.authenticated,
  }),
  { logout },
)(LoadedApp);
